import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Flame, Target, CheckCircle, Gift, Loader2, Clock, Trophy } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

export function DailyChallenges() {
  const { token, authHeaders } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);

  useEffect(() => {
    if (token) fetchChallenges();
    else setLoading(false);
  }, [token]);

  const fetchChallenges = async () => {
    try {
      const res = await axios.get(`${API}/challenges/today`, { headers: authHeaders() });
      setChallenges(res.data.challenges);
      setDate(res.data.date);
    } catch {}
    setLoading(false);
  };

  const claimReward = async (challengeId) => {
    setClaiming(challengeId);
    try {
      const res = await axios.post(`${API}/challenges/${challengeId}/claim`, null, { headers: authHeaders() });
      toast.success(`+${res.data.reward} Zorków za wyzwanie!`);
      fetchChallenges();
    } catch (err) { toast.error(err.response?.data?.detail || 'Błąd'); }
    setClaiming(null);
  };

  if (!token || loading) return null;
  if (challenges.length === 0) return null;

  const completedCount = challenges.filter(c => c.completed).length;

  return (
    <div className="bg-card border border-border/40 p-4 mb-6" data-testid="daily-challenges">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-heading text-base font-bold text-white flex items-center gap-2">
          <Target className="w-5 h-5 text-primary" /> Wyzwania dnia
        </h2>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-muted-foreground">{completedCount}/{challenges.length}</span>
          <div className="w-16 h-1.5 bg-secondary rounded-full overflow-hidden">
            <div className="h-full bg-primary transition-all duration-500" style={{ width: `${(completedCount / challenges.length) * 100}%` }} />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {challenges.map(ch => (
          <motion.div
            key={ch.id}
            layout
            className={`flex items-center gap-3 p-3 border transition-colors ${
              ch.completed
                ? ch.claimed ? 'border-border/20 bg-secondary/30 opacity-60' : 'border-primary/40 bg-primary/5'
                : 'border-border/30 bg-card'
            }`}
            data-testid={`challenge-${ch.id}`}
          >
            <div className={`w-9 h-9 flex-shrink-0 flex items-center justify-center border ${
              ch.completed ? 'border-primary bg-primary/20' : 'border-border/40'
            }`}>
              {ch.completed ? (
                <CheckCircle className="w-5 h-5 text-primary" />
              ) : (
                <span className="text-xs font-heading font-bold text-muted-foreground">{ch.progress}/{ch.target}</span>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white">{ch.title}</p>
              <p className="text-xs text-muted-foreground">{ch.description}</p>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-xs text-zorki font-heading font-bold">+{ch.reward} Z</span>
              {ch.completed && !ch.claimed && (
                <Button
                  onClick={() => claimReward(ch.id)}
                  disabled={claiming === ch.id}
                  className="h-7 text-[10px] bg-primary text-primary-foreground rounded-none px-3 font-heading uppercase"
                  data-testid={`claim-${ch.id}`}
                >
                  {claiming === ch.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Gift className="w-3 h-3 mr-1" /> Odbierz</>}
                </Button>
              )}
              {ch.claimed && (
                <span className="text-[10px] text-muted-foreground">Odebrano</span>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
